import React, { useState, useEffect, useRef } from "react";
import axios from "../../utils/axiosInstance";
import Button from "../ui/Button";
import TaskForm from "../TaskManagement/forms/TaskForm";
import { useT } from "../../contexts/TranslationContext";

const TASK_TYPE_TASK = "task";
const TASK_TYPE_SUBTASK = "subtask";
const TASK_TYPE_TASK_SET = "task_set";

/**
 * Project context modal for creating/editing tasks with subtasks
 * Uses shared TaskForm with project mode enabled.
 * Optional "context" mode: pass projects + initialProjectId + initialTaskType to show
 * editable Project and Task type (task set / task / sub-task) at top.
 */
const TaskBuilderModal = ({
  bundleId,
  projectId = "",
  isOpen,
  onClose,
  onSuccess,
  task = null,
  projects = null,
  initialProjectId = "",
  initialTaskType = TASK_TYPE_TASK
}) => {
  const formRef = useRef(null);
  const { t } = useT();
  const isContextMode = Array.isArray(projects) && projects.length > 0;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    weight: 1,
    estimatedHours: "",
    tags: [],
    subTasks: [],
    saveToRegistry: false,
    billingModel: "",
    hourlyRate: "",
    fixedPrice: "",
    source: ""
  });
  const [saving, setSaving] = useState(false);

  // Context mode state
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId || "");
  const [taskType, setTaskType] = useState(initialTaskType || TASK_TYPE_TASK);
  const [contextBundle, setContextBundle] = useState(null);
  const [contextBundleLoading, setContextBundleLoading] = useState(false);
  const [subtaskFormData, setSubtaskFormData] = useState({
    parentTaskId: "",
    title: "",
    description: "",
    weight: 1,
    estimatedHours: "",
    billingModel: "",
    hourlyRate: "",
    fixedPrice: "",
    source: ""
  });
  const [publishedTaskSets, setPublishedTaskSets] = useState([]);
  const [taskSetsLoading, setTaskSetsLoading] = useState(false);
  const [selectedTaskSetId, setSelectedTaskSetId] = useState("");

  const effectiveBundleId = contextBundle?._id ?? bundleId;
  const effectiveBundle = contextBundle ?? (bundleId ? null : null);
  const resolvedProjectId = selectedProjectId || projectId || "";

  useEffect(() => {
    if (isOpen && isContextMode) {
      setSelectedProjectId(initialProjectId || "");
      setTaskType(initialTaskType || TASK_TYPE_TASK);
    }
  }, [isOpen, isContextMode, initialProjectId, initialTaskType]);

  useEffect(() => {
    if (!isOpen || !isContextMode || !selectedProjectId) {
      setContextBundle(null);
      return;
    }
    setContextBundleLoading(true);
    axios
      .get(`/task-bundles/projects/${selectedProjectId}/task-bundle`)
      .then((res) => setContextBundle(res.data))
      .catch(() => setContextBundle(null))
      .finally(() => setContextBundleLoading(false));
  }, [isOpen, isContextMode, selectedProjectId]);

  useEffect(() => {
    if (!isOpen || !isContextMode || taskType !== TASK_TYPE_TASK) {
      setPublishedTaskSets([]);
      return;
    }
    setTaskSetsLoading(true);
    axios
      .get("/registry/task-sets", { params: { status: "published", limit: 100 } })
      .then((res) => setPublishedTaskSets(res.data.items || []))
      .catch(() => setPublishedTaskSets([]))
      .finally(() => setTaskSetsLoading(false));
  }, [isOpen, isContextMode, taskType]);

  useEffect(() => {
    if (isOpen && isContextMode) setSelectedTaskSetId("");
  }, [isOpen, isContextMode, taskType]);

  useEffect(() => {
    if (isOpen && task) {
      setFormData({
        ...task,
        subTasks: task.subTasks || [],
        saveToRegistry: task.saveToRegistry || false,
        showDescriptionInTimeReport: task.showDescriptionInTimeReport || false,
        requireDescriptionInTimeReport: task.requireDescriptionInTimeReport || false,
        photosNotRequiredInTimeReport: task.photosNotRequiredInTimeReport || false,
        billingModel: task.billingModel || "",
        hourlyRate: task.hourlyRate || "",
        fixedPrice: task.fixedPrice || "",
        source: task.source || ""
      });
    } else if (isOpen && !task) {
      setFormData({
        name: "",
        description: "",
        weight: 1,
        estimatedHours: "",
        tags: [],
        subTasks: [],
        saveToRegistry: false,
        showDescriptionInTimeReport: false,
        requireDescriptionInTimeReport: false,
        photosNotRequiredInTimeReport: false,
        billingModel: "",
        hourlyRate: "",
        fixedPrice: "",
        source: ""
      });
      if (isContextMode) {
        setSubtaskFormData({
          parentTaskId: "",
          title: "",
          description: "",
          weight: 1,
          estimatedHours: "",
          billingModel: "",
          hourlyRate: "",
          fixedPrice: "",
          source: ""
        });
      }
    }
  }, [isOpen, task, isContextMode]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "unset";
      };
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (taskType === TASK_TYPE_SUBTASK) {
      if (!subtaskFormData.title?.trim()) {
        alert("Sub-task title is required");
        return;
      }
      if (!subtaskFormData.parentTaskId || !effectiveBundleId) {
        alert("Please select a parent task and ensure the project has a task bundle.");
        return;
      }
      const taskIdx = (effectiveBundle?.tasks || []).findIndex(
        (t) => t._id.toString() === subtaskFormData.parentTaskId
      );
      if (taskIdx < 0) {
        alert("Invalid parent task");
        return;
      }

      setSaving(true);
      try {
        const res = await axios.post(
          `/task-bundles/${effectiveBundleId}/tasks/${taskIdx}/subtasks`,
          {
            title: subtaskFormData.title.trim(),
            description: subtaskFormData.description || "",
            weight: parseFloat(subtaskFormData.weight) || 1,
            estimatedHours: subtaskFormData.estimatedHours
              ? parseFloat(subtaskFormData.estimatedHours)
              : undefined,
            billingModel: subtaskFormData.billingModel || null,
            hourlyRate: subtaskFormData.billingModel === "hourly" && subtaskFormData.hourlyRate
              ? parseFloat(subtaskFormData.hourlyRate)
              : undefined,
            fixedPrice: subtaskFormData.billingModel === "fixed_price" && subtaskFormData.fixedPrice
              ? parseFloat(subtaskFormData.fixedPrice)
              : undefined,
            source: subtaskFormData.source || undefined
          }
        );
        const bundle = res.data;
        const parentTask = bundle?.tasks?.[taskIdx];
        const newSubtask = parentTask?.subtasks?.[parentTask.subtasks.length - 1];
        const newSubtaskId = newSubtask?._id?.toString?.();
        const parentTaskId = parentTask?._id?.toString?.();

        onSuccess && onSuccess({ subtaskId: newSubtaskId, parentTaskId, bundle });
        onClose();
      } catch (error) {
        console.error("Error creating subtask:", error);
        alert("Failed to create subtask");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (taskType === TASK_TYPE_TASK_SET) {
      return; // No create action for task set from this modal
    }

    if (!formData.name?.trim()) {
      alert("Task name is required");
      return;
    }

    const validSubtasks = (formData.subTasks || []).filter((st) => st.name?.trim());
    setSaving(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        weight: parseFloat(formData.weight) || 1,
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : undefined,
        tags: Array.isArray(formData.tags)
          ? formData.tags
          : formData.tags.split(",").map((t) => t.trim()).filter(Boolean),
        showDescriptionInTimeReport: formData.showDescriptionInTimeReport || false,
        requireDescriptionInTimeReport: formData.requireDescriptionInTimeReport || false,
        photosNotRequiredInTimeReport: formData.photosNotRequiredInTimeReport || false,
        subTasks: validSubtasks.map((st, index) => ({
          name: st.name,
          description: st.description || "",
          weight: parseFloat(st.weight) || 1,
          estimatedHours: st.estimatedHours ? parseFloat(st.estimatedHours) : undefined,
          showDescriptionInTimeReport: st.showDescriptionInTimeReport || false,
          requireDescriptionInTimeReport: st.requireDescriptionInTimeReport || false,
          registryId: st.registryId || undefined,
          sortOrder: index
        })),
        saveToRegistry: formData.saveToRegistry,
        billingModel: formData.billingModel || null,
        hourlyRate:
          formData.billingModel === "hourly" && formData.hourlyRate
            ? parseFloat(formData.hourlyRate)
            : undefined,
        fixedPrice:
          formData.billingModel === "fixed_price" && formData.fixedPrice
            ? parseFloat(formData.fixedPrice)
            : undefined,
        source: formData.source || undefined,
        ...(selectedTaskSetId && {
          taskSetRef: {
            setId: selectedTaskSetId,
            name: publishedTaskSets.find((s) => s._id === selectedTaskSetId)?.name
          }
        })
      };

      if (task) {
        await axios.put(`/task-bundles/${effectiveBundleId}/tasks/${task._id}`, payload);
        onSuccess && onSuccess();
      } else {
        const bundleIdForRequest =
          effectiveBundleId || (resolvedProjectId ? "new" : null);
        if (!bundleIdForRequest) {
          alert("No project or task bundle selected.");
          setSaving(false);
          return;
        }
        const res = await axios.post(
          `/task-bundles/${bundleIdForRequest}/tasks/ad-hoc`,
          {
            ...payload,
            ...(resolvedProjectId ? { projectId: resolvedProjectId } : {})
          }
        );
        const data = res.data;
        const createdTask = data?.bundle?.tasks?.[data.bundle.tasks.length - 1];
        const taskId = createdTask?._id?.toString?.();
        onSuccess && onSuccess(taskId ? { taskId, bundle: data?.bundle } : undefined);
      }
      onClose();
    } catch (error) {
      console.error("Error saving task:", error);
      alert("Failed to save task");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const showTaskForm = !isContextMode || taskType === TASK_TYPE_TASK;
  const showSubtaskForm = isContextMode && taskType === TASK_TYPE_SUBTASK;
  const showTaskSetNote = isContextMode && taskType === TASK_TYPE_TASK_SET;
  const canSubmitTask =
    showTaskForm &&
    (effectiveBundleId || resolvedProjectId);
  const canSubmitSubtask =
    showSubtaskForm && effectiveBundleId && subtaskFormData.parentTaskId && subtaskFormData.title?.trim();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">
            {task ? "Edit Task" : "Create Custom Task"}
          </h2>

          {isContextMode && (
            <div className="grid grid-cols-1 gap-4 mb-6 p-4 bg-gray-50 rounded-lg border">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  disabled={saving}
                >
                  <option value="">—</option>
                  {projects.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name} {p.address ? `(${p.address})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task type</label>
                <select
                  value={taskType}
                  onChange={(e) => setTaskType(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  disabled={saving}
                >
                  <option value={TASK_TYPE_TASK_SET}>Task set</option>
                  <option value={TASK_TYPE_TASK}>Task</option>
                  <option value={TASK_TYPE_SUBTASK}>Sub-task</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent task</label>
                <select
                  value={subtaskFormData.parentTaskId}
                  onChange={(e) =>
                    setSubtaskFormData((prev) => ({ ...prev, parentTaskId: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded"
                  disabled={
                    saving ||
                    contextBundleLoading ||
                    !effectiveBundle?.tasks?.length ||
                    taskType !== TASK_TYPE_SUBTASK
                  }
                  title={taskType !== TASK_TYPE_SUBTASK ? "Select Sub-task above to assign a parent task" : undefined}
                >
                  <option value="">{taskType === TASK_TYPE_SUBTASK ? "—" : "Select Sub-task to assign parent"}</option>
                  {(effectiveBundle?.tasks || []).map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.title ?? t.name ?? "—"}
                    </option>
                  ))}
                </select>
              </div>
              {taskType === TASK_TYPE_TASK && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Task set</label>
                  <select
                    value={selectedTaskSetId}
                    onChange={(e) => setSelectedTaskSetId(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    disabled={saving || taskSetsLoading}
                    title="Optionally link this task to a task set"
                  >
                    <option value="">—</option>
                    {publishedTaskSets.map((set) => (
                      <option key={set._id} value={set._id}>
                        {set.name ?? "—"}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {contextBundleLoading && (
                <p className="text-sm text-gray-500">Loading task bundle…</p>
              )}
              {!contextBundleLoading && selectedProjectId && !contextBundle && (
                <p className="text-sm text-amber-600">
                  No task bundle for this project. Create a task (below) to add one, or attach a task set from the project&apos;s Tasks section.
                </p>
              )}
            </div>
          )}

          {showTaskSetNote && (
            <p className="text-sm text-gray-600 mb-4">
              To add a new task set, attach one from the project&apos;s Tasks section.
            </p>
          )}

          <form
            ref={formRef}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e);
            }}
          >
            {showSubtaskForm && (
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title (required)</label>
                  <input
                    type="text"
                    value={subtaskFormData.title}
                    onChange={(e) =>
                      setSubtaskFormData((prev) => ({ ...prev, title: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Sub-task name"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={subtaskFormData.description}
                    onChange={(e) =>
                      setSubtaskFormData((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded"
                    rows={2}
                    disabled={saving}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={subtaskFormData.weight}
                      onChange={(e) =>
                        setSubtaskFormData((prev) => ({ ...prev, weight: e.target.value }))
                      }
                      className="w-full px-3 py-2 border rounded"
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimated hours</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={subtaskFormData.estimatedHours}
                      onChange={(e) =>
                        setSubtaskFormData((prev) => ({ ...prev, estimatedHours: e.target.value }))
                      }
                      className="w-full px-3 py-2 border rounded"
                      disabled={saving}
                    />
                  </div>
                </div>

                {/* Billing Configuration for subtask */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Billing (optional)</h3>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Billing Model</label>
                    <select
                      value={subtaskFormData.billingModel}
                      onChange={(e) =>
                        setSubtaskFormData((prev) => ({ ...prev, billingModel: e.target.value }))
                      }
                      className="w-full px-3 py-2 border rounded"
                      disabled={saving}
                    >
                      <option value="">Included in parent task</option>
                      <option value="hourly">Hourly/Ongoing</option>
                      <option value="fixed_price">Fixed Price</option>
                    </select>
                  </div>
                  {subtaskFormData.billingModel === "hourly" && (
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate (kr/hr)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={subtaskFormData.hourlyRate}
                        onChange={(e) =>
                          setSubtaskFormData((prev) => ({ ...prev, hourlyRate: e.target.value }))
                        }
                        placeholder="e.g., 850"
                        className="w-full px-3 py-2 border rounded"
                        disabled={saving}
                      />
                    </div>
                  )}
                  {subtaskFormData.billingModel === "fixed_price" && (
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Price (kr)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={subtaskFormData.fixedPrice}
                        onChange={(e) =>
                          setSubtaskFormData((prev) => ({ ...prev, fixedPrice: e.target.value }))
                        }
                        placeholder="e.g., 150000"
                        className="w-full px-3 py-2 border rounded"
                        disabled={saving}
                      />
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("taskBuilder.source")}</label>
                    <select
                      value={subtaskFormData.source}
                      onChange={(e) =>
                        setSubtaskFormData((prev) => ({ ...prev, source: e.target.value }))
                      }
                      className="w-full px-3 py-2 border rounded"
                      disabled={saving}
                    >
                      <option value="">{t("taskBuilder.sourceNotSet")}</option>
                      <option value="original_offer">{t("taskBuilder.sourceOriginalOffer")}</option>
                      <option value="variations_ata">{t("taskBuilder.sourceVariationsAta")}</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {showTaskForm && (
              <>
                <TaskForm
                  value={formData}
                  onChange={setFormData}
                  mode="project"
                  showSaveToRegistry={true}
                  disabled={saving}
                />

                {/* Billing Configuration Section */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-semibold mb-3">Billing Configuration</h3>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Model
                    </label>
                    <select
                      value={formData.billingModel}
                      onChange={(e) => setFormData({ ...formData, billingModel: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                      disabled={saving}
                    >
                      <option value="">Not set</option>
                      <option value="hourly">Hourly/Ongoing</option>
                      <option value="fixed_price">Fixed Price</option>
                    </select>
                  </div>

                  {formData.billingModel === "hourly" && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hourly Rate (kr/hr)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.hourlyRate}
                        onChange={(e) =>
                          setFormData({ ...formData, hourlyRate: e.target.value })
                        }
                        placeholder="e.g., 850"
                        className="w-full px-3 py-2 border rounded"
                        disabled={saving}
                      />
                    </div>
                  )}

                  {formData.billingModel === "fixed_price" && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fixed Price (kr)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.fixedPrice}
                        onChange={(e) =>
                          setFormData({ ...formData, fixedPrice: e.target.value })
                        }
                        placeholder="e.g., 150000"
                        className="w-full px-3 py-2 border rounded"
                        disabled={saving}
                      />
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("taskBuilder.source")}
                    </label>
                    <select
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                      disabled={saving}
                    >
                      <option value="">{t("taskBuilder.sourceNotSet")}</option>
                      <option value="original_offer">{t("taskBuilder.sourceOriginalOffer")}</option>
                      <option value="variations_ata">{t("taskBuilder.sourceVariationsAta")}</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={onClose} type="button">
                Cancel
              </Button>
              {(showTaskForm || showSubtaskForm) && (
                <Button
                  variant="solid"
                  type="button"
                  disabled={
                    saving ||
                    (showTaskForm && !canSubmitTask) ||
                    (showSubtaskForm && !canSubmitSubtask)
                  }
                  onClick={(e) => {
                    const syntheticEvent = { preventDefault: () => {} };
                    handleSubmit(syntheticEvent);
                  }}
                >
                  {saving
                    ? "Saving..."
                    : showSubtaskForm
                      ? "Add Sub-task"
                      : task
                        ? "Save Task"
                        : "Add Task"}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TaskBuilderModal;
